
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Edit, Save, X, Plus } from 'lucide-react';

interface EditableTagListProps {
  label: string;
  items: string[];
  onSave: (items: string[]) => void;
  className?: string;
}

export const EditableTagList: React.FC<EditableTagListProps> = ({
  label,
  items,
  onSave,
  className = ""
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localItems, setLocalItems] = useState([...items]);
  
  const handleSave = () => {
    onSave(localItems);
    setIsEditing(false);
  };
  
  const handleCancel = () => {
    setLocalItems([...items]);
    setIsEditing(false);
  };
  
  const handleItemChange = (index: number, value: string) => {
    const newItems = [...localItems];
    newItems[index] = value;
    setLocalItems(newItems);
  };
  
  const handleAddItem = () => {
    setLocalItems([...localItems, ""]);
  };
  
  const handleRemoveItem = (index: number) => {
    const newItems = [...localItems];
    newItems.splice(index, 1);
    setLocalItems(newItems);
  };
  
  if (!isEditing) {
    return (
      <div className={`relative group ${className}`}>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <Label className="block text-sm text-muted-foreground mb-1">{label}</Label>
            <div className="flex flex-wrap gap-2">
              {items.map((item, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-secondary rounded-full text-sm"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setIsEditing(true)}
            className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity ml-2"
          >
            <Edit className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`space-y-3 ${className}`}>
      <Label>{label}</Label>
      {localItems.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            value={item}
            onChange={(e) => handleItemChange(index, e.target.value)}
            className="flex-1"
          />
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleRemoveItem(index)}
            className="h-9 px-2"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleAddItem}
        className="flex items-center gap-1 mt-2"
      >
        <Plus className="h-3.5 w-3.5" />
        Add Item
      </Button>
      <div className="flex justify-end gap-2 mt-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleCancel}
        >
          <X className="h-3.5 w-3.5 mr-1" />
          Cancel
        </Button>
        <Button 
          size="sm" 
          onClick={handleSave}
        >
          <Save className="h-3.5 w-3.5 mr-1" />
          Save
        </Button>
      </div>
    </div>
  );
};
